import { CdkTrapFocus } from '@angular/cdk/a11y';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    OnDestroy,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
    fetchNotificationsStreamPage,
    markAllNotificationsAsRead,
    NotificationEvent,
    selectNotifications,
    selectOnlyUnread,
    selectUnreadNotificationsCount,
    setOnlyUnreadNotificationFilter as setOnlyUnreadNotificationsFilter,
} from '@interacta-shared/data-access-notification';
import {
    IconButtonComponent,
    LoadMoreComponent,
    ScrollTrackerEvent,
    SeparatorComponent,
    ToggleComponent,
} from '@interacta-shared/ui';
import {
    InvisibleScrollV2Component,
    NoResultComponent,
} from '@interacta-shared/ui-common';
import { PaginatedList } from '@interacta-shared/util';
import { focusFirstNewChild } from '@interacta-shared/util-common';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { NotificationPanelService } from '../../services/notification-panel.service';
import { NotificationPanelItemComponent } from '../notification-panel-item/notification-panel-item.component';

@Component({
    selector: 'interacta-notification-panel-overlay',
    imports: [
        IconButtonComponent,
        ToggleComponent,
        TranslatePipe,
        LoadMoreComponent,
        NoResultComponent,
        SeparatorComponent,
        NotificationPanelItemComponent,
        InvisibleScrollV2Component,
    ],
    templateUrl: './notification-panel-overlay.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    hostDirectives: [CdkTrapFocus],
    host: {
        'aria-modal': 'true',
        '[attr.aria-labelledby]': 'titleId',
        class: 'flex h-screen flex-col items-end py-16',
        role: 'dialog',
        '(click)': 'close()',
    },
})
export class NotificationPanelOverlayComponent implements OnDestroy {
    private readonly store = inject(Store);
    private readonly notificationPanelService = inject(
        NotificationPanelService,
    );
    private readonly cdkTrapFocus = inject(CdkTrapFocus, { self: true });

    private readonly notificationPanel =
        viewChild.required<ElementRef<HTMLElement>>('notificationPanel');

    readonly onlyUnread = this.store.selectSignal(selectOnlyUnread);
    readonly unreadNotificationsCount = this.store.selectSignal(
        selectUnreadNotificationsCount,
    );
    readonly notifications = this.store.selectSignal(selectNotifications);
    private changes?: MutationObserver;

    readonly showNoResult = computed(() => {
        return (
            this.notifications().nextPageToken.tag !== 'firstLoading' &&
            !this.notifications().isFetching &&
            !this.notifications().list.length
        );
    });

    readonly showLoadMore = computed(() => {
        return (
            this.notifications().nextPageToken.tag === 'regularLoading' ||
            this.notifications().isFetching
        );
    });

    readonly filterStatusMessage = computed<{
        key: string;
        params: Record<string, unknown>;
    } | null>(() => {
        const n = this.notifications();
        if (n.nextPageToken.tag === 'firstLoading' || n.isFetching) {
            return null;
        }
        if (!n.list.length) {
            return {
                key: this.onlyUnread()
                    ? 'SHARED.NOTIFICATIONS.NO_UNREAD_NOTIFICATIONS'
                    : 'SHARED.NOTIFICATIONS.EMPTY_PAGE',
                params: {},
            };
        }
        const unreadCount = this.unreadNotificationsCount();
        if (this.onlyUnread()) {
            return {
                key: 'SHARED.NOTIFICATIONS.UNREAD_COUNT',
                params: { count: unreadCount },
            };
        }
        return {
            key: 'SHARED.NOTIFICATIONS.FILTER_RESULT_ALL',
            params: { unreadCount },
        };
    });

    readonly titleId = 'notification-panel-overlay-title';

    constructor() {
        this.cdkTrapFocus.autoCapture = true;
        toObservable(this.notificationPanel)
            .pipe(takeUntilDestroyed())
            .subscribe((notificationPanel) =>
                notificationPanel.nativeElement.addEventListener(
                    'click',
                    ($event) => $event.stopPropagation(),
                ),
            );
    }

    close(): void {
        this.notificationPanelService.close();
    }

    setOnlyUnreadNotificationFilter(onlyUnread: boolean): void {
        this.store.dispatch(setOnlyUnreadNotificationsFilter({ onlyUnread }));
    }

    markAsRead(notification: NotificationEvent): void {
        this.notificationPanelService.markNotificationAsRead(notification);
    }

    markAllAsRead(): void {
        this.store.dispatch(markAllNotificationsAsRead());
    }
    nextPage(
        event: ScrollTrackerEvent,
        notifications: PaginatedList<NotificationEvent>,
        onlyUnread: boolean,
    ): void {
        if (event.endReached) {
            this.loadMorePastNotification(notifications, onlyUnread);
        }
    }

    loadMorePastNotification(
        notification: PaginatedList<NotificationEvent>,
        onlyUnread: boolean,
        focusNext?: HTMLElement,
    ): void {
        if (
            notification.nextPageToken.tag === 'regularLoading' &&
            !notification.isFetching
        ) {
            if (focusNext) {
                this.changes = focusFirstNewChild({
                    container: focusNext,
                    currentChildCount: notification.list.length,
                    pendingObserver: this.changes,
                });
            }
            this.store.dispatch(
                fetchNotificationsStreamPage({
                    pageToken: notification.nextPageToken.nextPageToken,
                    onlyUnread,
                }),
            );
        }
    }

    ngOnDestroy(): void {
        this.changes?.disconnect();
    }
}
